import React, { useState, useEffect, useCallback, useRef } from 'react';
import Select from 'react-select';
import { Package, Barcode, DollarSign, Hash, FileText, Tag, Warehouse, Plus, Trash2, Link2, Printer, Percent, Receipt } from 'lucide-react';
import Input from '../../components/forms/Input';
import Button from '../../components/ui/Button';
import { useApp } from '../../context/AppContext';
import './ProductForm.css';

const ProductForm = ({ producto, categorias, almacenes, onSubmit, onCancel, isLoading = false, isEdit = false, renderHeaderToggle = null }) => {
    const { showToast } = useApp();

    // Refs para el primer input de cada pestaña
    const nombreInputRef = useRef(null);
    const precioListaInputRef = useRef(null);
    const stockInputRef = useRef(null);

    // Estado para tabs
    const [activeTab, setActiveTab] = useState('basico');

    const [formData, setFormData] = useState({
        nombre: '',
        descripcion: '',
        codigo_corto: '',
        codigo_barras: '',
        cve_cfdi: '',
        objeto_imp: '02',
        tasa_iva: '0.16',
        categoria_id: '',
        almacen_id: '',
        precio_compra: '',
        precio_lista: '',
        precio1: '',
        precio2: '',
        precio3: '',
        precio4: '',
        precio5: '',
        precio6: '',
        stock: '',
        stock_minimo: '',
        unidad_medida: 'pqt',
        impresora: '',
        activo: true,
        es_base: true
    });

    const [errors, setErrors] = useState({});

    // Estados para equivalencias
    const [useEquivalencias, setUseEquivalencias] = useState(false);
    const [equivalencias, setEquivalencias] = useState([]);

    // Estados para crear productos equivalentes (tabla múltiple)
    const [creandoEquivalente, setCreandoEquivalente] = useState(false);

    // Unidades disponibles para equivalencias
    const unidadesEquivalentes = [
        { key: 'kg', label: 'Kilogramo', unidad: 'kg' },
        { key: 'caja', label: 'Caja', unidad: 'cja' },

        { key: 'bulto', label: 'Bulto', unidad: 'blt' },
        { key: 'paquete', label: 'Paquete', unidad: 'pqt' },
        { key: 'mil', label: 'Millar', unidad: 'mil' },
        { key: 'pz', label: 'Pieza', unidad: 'pz' },





    ];

    // Estado para la tabla de equivalentes
    const [tablaEquivalentes, setTablaEquivalentes] = useState(
        unidadesEquivalentes.map(u => ({
            key: u.key,
            label: u.label,
            unidad: u.unidad,
            activo: false,
            equivalencia: '',  // Factor de conversión (ej: 12 piezas = 1 caja)
            volumen: '',       // Cantidad mínima para descuento por volumen
            descuento: '',     // Porcentaje de descuento por volumen
            costo: '',
            precio_lista: '',
            precio1: '',
            precio2: '',
            precio3: '',
            precio4: '',
            precio5: '',
            precio6: '',
            codigo_corto: '',
            codigo_asociado: ''
        }))
    );

    // Estados para descuentos por volumen
    const [useDescuentosVolumen, setUseDescuentosVolumen] = useState(false);
    const [descuentosVolumen, setDescuentosVolumen] = useState([]);

    // Estado para impresoras del sistema
    const [impresoras, setImpresoras] = useState([]);
    const [loadingImpresoras, setLoadingImpresoras] = useState(false);

    // Cargar impresoras del sistema al montar el componente
    const loadImpresoras = useCallback(async () => {
        if (!window.electron?.ipcRenderer) {
            console.warn('No se encontró Electron IPC, las impresoras no estarán disponibles');
            return;
        }

        try {
            setLoadingImpresoras(true);
            const printers = await window.electron.ipcRenderer.invoke('get-all-printers');
            if (Array.isArray(printers)) {
                setImpresoras(printers);
            }
        } catch (error) {
            console.error('Error al cargar impresoras:', error);
        } finally {
            setLoadingImpresoras(false);
        }
    }, []);

    // Cargar descuentos por volumen existentes
    const loadExistingDescuentos = useCallback(async () => {
        if (!isEdit || !producto?.id) return;

        try {
            const apiService = (await import('../../services/apiService')).default;
            const result = await apiService.getDescuentosVolumen(producto.id);

            if (result.success && result.data.length > 0) {
                setDescuentosVolumen(result.data);
                setUseDescuentosVolumen(true);
            }
        } catch (error) {
            console.warn('No se pudieron cargar los descuentos por volumen:', error);
        }
    }, [isEdit, producto?.id]);

    // Cargar equivalencias existentes
    const loadExistingEquivalencias = useCallback(async () => {
        if (!isEdit || !producto?.id) return;

        try {
            const apiService = (await import('../../services/apiService')).default;
            const result = await apiService.getEquivalenciasProducto(producto.id);

            if (result.success && result.data.length > 0) {
                setEquivalencias(result.data);
                setUseEquivalencias(true);
            }
        } catch (error) {
            console.warn('No se pudieron cargar las equivalencias:', error);
        }
    }, [isEdit, producto?.id]);

    // Eliminar equivalencia
    const eliminarEquivalencia = (index) => {
        setEquivalencias(equivalencias.filter((_, i) => i !== index));
    };

    // Actualizar cantidad de equivalencia
    const actualizarCantidadEquivalencia = (index, cantidad) => {
        const nuevasEquivalencias = [...equivalencias];
        nuevasEquivalencias[index].cantidad = cantidad;
        setEquivalencias(nuevasEquivalencias);
    };

    // Actualizar precio de equivalencia existente
    const actualizarPrecioEquivalencia = (index, campo, valor) => {
        const nuevasEquivalencias = [...equivalencias];
        nuevasEquivalencias[index][campo] = valor;
        setEquivalencias(nuevasEquivalencias);
    };

    // Toggle activar/desactivar fila de equivalente
    const toggleEquivalenteActivo = (key) => {
        setTablaEquivalentes(prev => prev.map(item => {
            if (item.key === key) {
                const nuevoActivo = !item.activo;
                // Si se activa y no tiene código corto, auto-generar basado en el producto base
                if (nuevoActivo && !item.codigo_corto && formData.codigo_corto) {
                    return {
                        ...item,
                        activo: nuevoActivo,
                        codigo_corto: `${formData.codigo_corto}-${item.unidad.toUpperCase()}`
                    };
                }
                return { ...item, activo: nuevoActivo };
            }
            return item;
        }));
    };

    // Actualizar campo de un equivalente en la tabla
    const actualizarCampoEquivalente = (key, campo, valor) => {
        setTablaEquivalentes(prev => prev.map(item =>
            item.key === key ? { ...item, [campo]: valor } : item
        ));
    };


    // Crear todos los productos equivalentes activos (función legacy, ahora se hace en el submit)
    const crearProductosEquivalentes = async () => {
        const equivalentesActivos = tablaEquivalentes.filter(eq => eq.activo);

        if (equivalentesActivos.length === 0) {
            showToast('Debes activar al menos una unidad para crear equivalentes', 'warning');
            return;
        }

        // Validar que los activos tengan precio de lista
        for (const eq of equivalentesActivos) {
            if (!eq.precio_lista || parseFloat(eq.precio_lista) <= 0) {
                showToast(`El precio de lista es requerido para "${eq.label}"`, 'warning');
                return;
            }
        }

        setCreandoEquivalente(true);

        try {
            const apiService = (await import('../../services/apiService')).default;
            const nuevasEquivalencias = [];

            for (const eq of equivalentesActivos) {
                const nombreProducto = `${formData.nombre} (${eq.label})`;
                // Usar código corto personalizado si se ingresó, o generar uno automáticamente
                const codigoCorto = eq.codigo_corto || (formData.codigo_corto ? `${formData.codigo_corto}-${eq.unidad.toUpperCase()}` : '');

                const nuevoProducto = {
                    nombre: nombreProducto,
                    codigo_corto: codigoCorto || null,
                    codigo_barras: eq.codigo_asociado || null,
                    categoria_id: formData.categoria_id || null,
                    almacen_id: formData.almacen_id || null,
                    unidad_medida: eq.unidad,
                    precio_compra: parseFloat(eq.costo) || 0,
                    precio_lista: parseFloat(eq.precio_lista),
                    precio1: parseFloat(eq.precio1) || 0,
                    precio2: parseFloat(eq.precio2) || 0,
                    precio3: parseFloat(eq.precio3) || 0,
                    precio4: parseFloat(eq.precio4) || 0,
                    precio5: parseFloat(eq.precio5) || 0,
                    precio6: parseFloat(eq.precio6) || 0,
                    stock: 0,
                    stock_minimo: 0,
                    activo: 1,
                    es_base: 0
                };

                const result = await apiService.crearProducto(nuevoProducto);

                if (result.success) {
                    nuevasEquivalencias.push({
                        producto_equivalente_id: result.data.id,
                        producto_equivalente_nombre: nombreProducto,
                        codigo_corto: codigoCorto,
                        codigo_barras: eq.codigo_asociado || null,
                        cantidad: parseFloat(eq.equivalencia) || 1,  // Factor de equivalencia
                        volumen: parseFloat(eq.volumen) || 0,        // Cantidad mínima para descuento
                        descuento: parseFloat(eq.descuento) || 0,    // Porcentaje de descuento
                        unidad_medida: eq.unidad,
                        stock: 0,
                        costo: parseFloat(eq.costo) || 0,
                        precio_lista: parseFloat(eq.precio_lista) || 0,
                        precio1: parseFloat(eq.precio1) || 0,
                        precio2: parseFloat(eq.precio2) || 0,
                        precio3: parseFloat(eq.precio3) || 0,
                        precio4: parseFloat(eq.precio4) || 0,
                        precio5: parseFloat(eq.precio5) || 0,
                        precio6: parseFloat(eq.precio6) || 0
                    });
                }
            }

            // Agregar todas las nuevas equivalencias
            setEquivalencias([...equivalencias, ...nuevasEquivalencias]);

            // Resetear la tabla de nuevos equivalentes
            setTablaEquivalentes(
                unidadesEquivalentes.map(u => ({
                    key: u.key,
                    label: u.label,
                    unidad: u.unidad,
                    activo: false,
                    equivalencia: '',
                    volumen: '',
                    descuento: '',
                    costo: '',
                    precio_lista: '',
                    precio1: '',
                    precio2: '',
                    precio3: '',
                    precio4: '',
                    precio5: '',
                    precio6: '',
                    codigo_corto: '',
                    codigo_asociado: ''
                }))
            );

            showToast(`Se crearon ${nuevasEquivalencias.length} producto(s) equivalente(s) exitosamente`, 'success');
        } catch (error) {
            console.error('Error al crear productos equivalentes:', error);
            showToast('Error al crear productos equivalentes', 'error');
        } finally {
            setCreandoEquivalente(false);
        }
    };

    // Agregar descuento por volumen
    const agregarDescuento = () => {
        setDescuentosVolumen([...descuentosVolumen, {
            cantidad_minima: '',
            descuento_porcentaje: ''
        }]);
    };

    // Eliminar descuento por volumen
    const eliminarDescuento = (index) => {
        setDescuentosVolumen(descuentosVolumen.filter((_, i) => i !== index));
    };

    // Actualizar descuento por volumen
    const actualizarDescuento = (index, field, value) => {
        const nuevosDescuentos = [...descuentosVolumen];
        nuevosDescuentos[index][field] = value;
        setDescuentosVolumen(nuevosDescuentos);
    };

    useEffect(() => {
        if (isEdit && producto) {
            const initialFormData = {
                nombre: producto.nombre || '',
                descripcion: producto.descripcion || '',
                codigo_corto: producto.codigo_corto || '',
                codigo_barras: producto.codigo_barras || '',
                cve_cfdi: producto.cve_cfdi || '',
                objeto_imp: producto.objeto_imp || '02',
                tasa_iva: producto.tasa_iva !== undefined ? String(producto.tasa_iva) : '0.16',
                categoria_id: producto.categoria_id || '',
                almacen_id: producto.almacen_id || '',
                precio_compra: producto.precio_compra !== undefined && producto.precio_compra !== null ? producto.precio_compra : '',
                precio_lista: producto.precio_lista !== undefined && producto.precio_lista !== null ? producto.precio_lista : '',
                precio1: producto.precio1 !== undefined && producto.precio1 !== null ? producto.precio1 : '',
                precio2: producto.precio2 !== undefined && producto.precio2 !== null ? producto.precio2 : '',
                precio3: producto.precio3 !== undefined && producto.precio3 !== null ? producto.precio3 : '',
                precio4: producto.precio4 !== undefined && producto.precio4 !== null ? producto.precio4 : '',
                precio5: producto.precio5 !== undefined && producto.precio5 !== null ? producto.precio5 : '',
                precio6: producto.precio6 !== undefined && producto.precio6 !== null ? producto.precio6 : '',
                stock: producto.stock !== undefined && producto.stock !== null ? producto.stock : '',
                stock_minimo: producto.stock_minimo !== undefined && producto.stock_minimo !== null ? producto.stock_minimo : '',
                unidad_medida: producto.unidad_medida || 'pz',
                impresora: producto.impresora || '',
                activo: producto.activo !== undefined ? producto.activo : true,
                es_base: producto.es_base !== undefined ? producto.es_base : false
            };

            setFormData(initialFormData);
        }
    }, [isEdit, producto]);

    // Cargar descuentos existentes
    useEffect(() => {
        if (isEdit && producto?.id) {
            loadExistingDescuentos();
        }
    }, [isEdit, producto?.id, loadExistingDescuentos]);

    // Cargar equivalencias existentes
    useEffect(() => {
        if (isEdit && producto?.id) {
            loadExistingEquivalencias();
        }
    }, [isEdit, producto?.id, loadExistingEquivalencias]);

    // Cargar impresoras del sistema
    useEffect(() => {
        loadImpresoras();
    }, [loadImpresoras]);

    // Focus en el primer input de cada pestaña
    useEffect(() => {
        setTimeout(() => {
            switch (activeTab) {
                case 'basico':
                    nombreInputRef.current?.focus();
                    break;
                case 'precios':
                    precioListaInputRef.current?.focus();
                    break;
                case 'inventario':
                    stockInputRef.current?.focus();
                    break;
                default:
                    break;
            }
        }, 100);
    }, [activeTab]);

    // Renderizar toggle en header si se proporciona la función
    useEffect(() => {
        if (renderHeaderToggle) {
            renderHeaderToggle(
                <label className="toggle-switch-form">
                    <input
                        type="checkbox"
                        checked={formData.activo}
                        onChange={(e) => handleInputChange('activo', e.target.checked)}
                    />
                    <span className="toggle-slider-form"></span>
                    <span className="toggle-label">{formData.activo ? 'Activo' : 'Inactivo'}</span>
                </label>
            );
        }
    }, [renderHeaderToggle, formData.activo]);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: null
            }));
        }
    };

    // Manejar Enter para mover al siguiente input (como Tab)
    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            const tagName = e.target.tagName.toLowerCase();
            const inputType = e.target.type?.toLowerCase();

            // Solo actuar en inputs de texto, número, etc. (no en botones, textareas, etc.)
            if (tagName === 'input' && !['submit', 'button', 'checkbox', 'radio'].includes(inputType)) {
                e.preventDefault();
                const form = e.target.form;
                if (form) {
                    const inputs = Array.from(form.querySelectorAll(
                        'input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]):not([type="submit"]):not([type="button"]):not([disabled])'
                    ));
                    const currentIndex = inputs.indexOf(e.target);
                    if (currentIndex > -1 && currentIndex < inputs.length - 1) {
                        inputs[currentIndex + 1].focus();
                    }
                }
            }
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.nombre?.trim()) {
            newErrors.nombre = 'El nombre es requerido';
        }

        if (!formData.categoria_id) {
            newErrors.categoria_id = 'La categoría es requerida';
        }

        // Precios son opcionales - si están vacíos se guardan como 0
        if (formData.precio_compra && parseFloat(formData.precio_compra) < 0) {
            newErrors.precio_compra = 'El costo no puede ser negativo';
        }

        if (formData.precio_lista && parseFloat(formData.precio_lista) < 0) {
            newErrors.precio_lista = 'El precio de lista no puede ser negativo';
        }

        if (formData.stock && parseInt(formData.stock) < 0) {
            newErrors.stock = 'El stock no puede ser negativo';
        }

        if (formData.stock_minimo && parseInt(formData.stock_minimo) < 0) {
            newErrors.stock_minimo = 'El stock mínimo no puede ser negativo';
        }

        // Validar descuentos por volumen
        if (useDescuentosVolumen && descuentosVolumen.length > 0) {
            descuentosVolumen.forEach((descuento, index) => {
                if (!descuento.cantidad_minima || parseFloat(descuento.cantidad_minima) <= 0) {
                    newErrors[`descuento_${index}_cantidad`] = 'Cantidad requerida';
                }
                if (!descuento.descuento_porcentaje || parseFloat(descuento.descuento_porcentaje) <= 0 || parseFloat(descuento.descuento_porcentaje) > 100) {
                    newErrors[`descuento_${index}_porcentaje`] = 'Porcentaje inválido (1-100)';
                }
            });
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        // Preparar datos del producto
        const submitData = {
            nombre: formData.nombre,
            descripcion: formData.descripcion || '',
            codigo_corto: formData.codigo_corto || '',
            codigo_barras: formData.codigo_barras || '',
            cve_cfdi: formData.cve_cfdi || null,
            objeto_imp: formData.objeto_imp || '02',
            tasa_iva: formData.tasa_iva === 'ieps' ? 'ieps' : (parseFloat(formData.tasa_iva) || 0.16),
            categoria_id: parseInt(formData.categoria_id),
            almacen_id: formData.almacen_id ? parseInt(formData.almacen_id) : null,
            precio_compra: parseFloat(formData.precio_compra) || 0,
            precio_lista: parseFloat(formData.precio_lista) || 0,
            precio1: parseFloat(formData.precio1) || 0,
            precio2: parseFloat(formData.precio2) || 0,
            precio3: parseFloat(formData.precio3) || 0,
            precio4: parseFloat(formData.precio4) || 0,
            precio5: parseFloat(formData.precio5) || 0,
            precio6: parseFloat(formData.precio6) || 0,
            stock: parseInt(formData.stock) || 0,
            stock_minimo: parseInt(formData.stock_minimo) || 0,
            unidad_medida: formData.unidad_medida,
            impresora: formData.impresora || null,
            activo: formData.activo ? 1 : 0,
            es_base: formData.es_base ? 1 : 0
        };

        // Preparar datos de descuentos por volumen
        const descuentosData = useDescuentosVolumen ? descuentosVolumen.filter(d =>
            d.cantidad_minima && d.descuento_porcentaje
        ).map(d => ({
            cantidad_minima: parseFloat(d.cantidad_minima),
            descuento_porcentaje: parseFloat(d.descuento_porcentaje)
        })) : [];

        // Preparar datos de equivalencias si está activado
        const equivalenciasData = useEquivalencias ? equivalencias.map(eq => ({
            producto_equivalente_id: eq.producto_equivalente_id,
            cantidad: parseFloat(eq.cantidad) || 1,           // Factor de equivalencia
            volumen: parseFloat(eq.volumen) || 0,             // Cantidad mínima para descuento
            descuento: parseFloat(eq.descuento) || 0,         // Porcentaje de descuento
            codigo_corto: eq.codigo_corto || null,
            codigo_barras: eq.codigo_barras || null,
            costo: parseFloat(eq.costo) || 0,
            precio_lista: parseFloat(eq.precio_lista) || 0,
            precio1: parseFloat(eq.precio1) || 0,
            precio2: parseFloat(eq.precio2) || 0,
            precio3: parseFloat(eq.precio3) || 0,
            precio4: parseFloat(eq.precio4) || 0,
            precio5: parseFloat(eq.precio5) || 0,
            precio6: parseFloat(eq.precio6) || 0
        })) : [];

        // Preparar datos de nuevos equivalentes a crear (desde tablaEquivalentes)
        const nuevosEquivalentes = useEquivalencias ? tablaEquivalentes
            .filter(eq => eq.activo)
            .map(eq => ({
                label: eq.label,
                unidad: eq.unidad,
                equivalencia: parseFloat(eq.equivalencia) || 1,
                volumen: parseFloat(eq.volumen) || 0,
                descuento: parseFloat(eq.descuento) || 0,
                costo: parseFloat(eq.costo) || 0,
                precio_lista: parseFloat(eq.precio_lista) || 0,
                precio1: parseFloat(eq.precio1) || 0,
                precio2: parseFloat(eq.precio2) || 0,
                precio3: parseFloat(eq.precio3) || 0,
                precio4: parseFloat(eq.precio4) || 0,
                precio5: parseFloat(eq.precio5) || 0,
                precio6: parseFloat(eq.precio6) || 0,
                codigo_corto: eq.codigo_corto || '',
                codigo_barras: eq.codigo_asociado || ''
            })) : [];

        onSubmit(submitData, descuentosData, equivalenciasData, nuevosEquivalentes);
    };

    const unidadesMedida = [
        { value: 'pqt', label: 'Paquete' },
        { value: 'pz', label: 'Pieza' },
        { value: 'kg', label: 'Kilogramo' },
        { value: 'cja', label: 'Caja' },
        { value: 'btl', label: 'Bulto' },


        { value: 'mil', label: 'Millar' },


    ];

    // Estilos personalizados para react-select
    const selectStyles = {
        control: (base, state) => ({
            ...base,
            minHeight: '2.5rem',
            fontSize: '0.875rem',
            borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
            boxShadow: state.isFocused ? '0 0 0 1px #3b82f6' : 'none',
            '&:hover': { borderColor: '#3b82f6' }
        }),
        menu: (base) => ({
            ...base,
            zIndex: 9999,
            fontSize: '0.875rem'
        }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected ? '#3b82f6' : state.isFocused ? '#eff6ff' : 'white',
            color: state.isSelected ? 'white' : '#374151',
            padding: '0.5rem 0.75rem',
            cursor: 'pointer'
        }),
        placeholder: (base) => ({
            ...base,
            color: '#9ca3af'
        }),
        singleValue: (base) => ({
            ...base,
            color: '#374151'
        })
    };

    // Convertir categorías para react-select
    const categoriasOptions = categorias.map(cat => ({ value: cat.id, label: cat.nombre }));

    // Convertir almacenes para react-select
    const almacenesOptions = almacenes.map(alm => ({ value: alm.id, label: alm.nombre }));

    // Convertir impresoras para react-select
    const impresorasOptions = impresoras.map(printer => ({
        value: printer.name,
        label: printer.name + (printer.isDefault ? ' (Predeterminada)' : '')
    }));

    // Calcular precio con descuento para vista previa
    const calcularPrecioConDescuento = (porcentaje) => {
        const precioLista = parseFloat(formData.precio_lista) || 0;
        return precioLista * (1 - (parseFloat(porcentaje) || 0) / 100);
    };

    // Opciones de objeto de impuesto SAT
    const objetosImpuesto = [
        { value: '01', label: '01 - No objeto de impuesto' },
        { value: '02', label: '02 - Sí objeto de impuesto' },
        { value: '03', label: '03 - Sí objeto, no obligado al desglose' }
    ];

    // Opciones de tasa IVA
    const tasasIVA = [
        { value: '0.16', label: '16% - Tasa general' },
        { value: '0', label: '0% - Tasa cero' },
        { value: 'exento', label: 'Exento de IVA' },
        { value: 'ieps', label: 'IEPS' }
    ];

    return (
        <form onSubmit={handleSubmit} onKeyDown={handleKeyDown} className="product-form">
            {/* Tabs de navegación */}
            <div className="product-form-tabs">
                <button
                    type="button"
                    className={`product-form-tab ${activeTab === 'basico' ? 'active' : ''}`}
                    onClick={() => setActiveTab('basico')}
                >
                    <Package size={16} />
                    Información Básica
                </button>
                <button
                    type="button"
                    className={`product-form-tab ${activeTab === 'precios' ? 'active' : ''}`}
                    onClick={() => setActiveTab('precios')}
                >
                    <DollarSign size={16} />
                    Precios
                </button>
                <button
                    type="button"
                    className={`product-form-tab ${activeTab === 'inventario' ? 'active' : ''}`}
                    onClick={() => setActiveTab('inventario')}
                >
                    <Hash size={16} />
                    Inventario
                </button>
                <button
                    type="button"
                    className={`product-form-tab ${activeTab === 'descuentos' ? 'active' : ''}`}
                    onClick={() => setActiveTab('descuentos')}
                >
                    <Percent size={16} />
                    Descuentos
                </button>
                <button
                    type="button"
                    className={`product-form-tab ${activeTab === 'equivalencias' ? 'active' : ''}`}
                    onClick={() => setActiveTab('equivalencias')}
                >
                    <Link2 size={16} />
                    Equivalencias
                </button>
            </div>

            {/* Contenido de tabs */}
            <div className="product-form-tab-content">
                {/* Tab: Información Básica */}
                {activeTab === 'basico' && (
                    <div className="form-section">
                        <h4 className="section-title">
                            <Package size={16} />
                            Datos del Producto
                        </h4>

                        {/* Fila 1: Nombre, Código Corto, Código de Barras */}
                        <div className="form-row-3">
                            <div className="form-group">
                                <Input
                                    ref={nombreInputRef}
                                    label="Nombre del Producto *"
                                    value={formData.nombre}
                                    onChange={(e) => handleInputChange('nombre', e.target.value)}
                                    error={errors.nombre}
                                    placeholder="Nombre del producto"
                                    icon={<Package size={12} />}
                                    size="xs"
                                />
                            </div>
                            <div className="form-group">
                                <Input
                                    label="Código Corto"
                                    value={formData.codigo_corto}
                                    onChange={(e) => handleInputChange('codigo_corto', e.target.value)}
                                    placeholder="Código interno"
                                    icon={<Hash size={12} />}
                                    size="xs"
                                />
                            </div>
                            <div className="form-group">
                                <Input
                                    label="Código de Barras"
                                    value={formData.codigo_barras}
                                    onChange={(e) => handleInputChange('codigo_barras', e.target.value)}
                                    placeholder="Código de barras"
                                    icon={<Barcode size={12} />}
                                    size="xs"
                                />
                            </div>
                        </div>

                        {/* Fila 2: Categoría, Almacén, Unidad de Medida */}
                        <div className="form-row-3">
                            <div className="form-group">
                                <label className="form-label">
                                    <Tag size={12} />
                                    Categoría *
                                </label>
                                <Select
                                    value={categoriasOptions.find(opt => opt.value === formData.categoria_id) || null}
                                    onChange={(opt) => handleInputChange('categoria_id', opt ? opt.value : '')}
                                    options={categoriasOptions}
                                    placeholder="Buscar categoría..."
                                    isClearable
                                    styles={selectStyles}
                                    noOptionsMessage={() => 'Sin resultados'}
                                    className={errors.categoria_id ? 'react-select-error' : ''}
                                />
                                {errors.categoria_id && <span className="error-message">{errors.categoria_id}</span>}
                            </div>
                            <div className="form-group">
                                <label className="form-label">
                                    <Warehouse size={12} />
                                    Almacén
                                </label>
                                <Select
                                    value={almacenesOptions.find(opt => opt.value === formData.almacen_id) || null}
                                    onChange={(opt) => handleInputChange('almacen_id', opt ? opt.value : '')}
                                    options={almacenesOptions}
                                    placeholder="Buscar almacén..."
                                    isClearable
                                    styles={selectStyles}
                                    noOptionsMessage={() => 'Sin resultados'}
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">
                                    <FileText size={12} />
                                    Unidad de Medida
                                </label>
                                <Select
                                    value={unidadesMedida.find(opt => opt.value === formData.unidad_medida) || null}
                                    onChange={(opt) => handleInputChange('unidad_medida', opt ? opt.value : 'pqt')}
                                    options={unidadesMedida}
                                    placeholder="Buscar unidad..."
                                    styles={selectStyles}
                                    noOptionsMessage={() => 'Sin resultados'}
                                />
                            </div>
                        </div>

                        {/* Fila 3: Costo, Impresora, Producto Base */}
                        <div className="form-row-3">
                            <div className="form-group">
                                <Input
                                    type="number"
                                    label="Costo"
                                    value={formData.precio_compra}
                                    onChange={(e) => handleInputChange('precio_compra', e.target.value)}
                                    error={errors.precio_compra}
                                    placeholder="0.00"
                                    step="0.01"
                                    min="0"
                                    icon={<DollarSign size={12} />}
                                    size="xs"
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">
                                    <Printer size={12} />
                                    Impresora
                                </label>
                                <Select
                                    value={impresorasOptions.find(opt => opt.value === formData.impresora) || null}
                                    onChange={(opt) => handleInputChange('impresora', opt ? opt.value : '')}
                                    options={impresorasOptions}
                                    placeholder="Buscar impresora..."
                                    isClearable
                                    isLoading={loadingImpresoras}
                                    isDisabled={loadingImpresoras}
                                    styles={selectStyles}
                                    noOptionsMessage={() => 'Sin impresoras'}
                                    loadingMessage={() => 'Cargando...'}
                                />
                            </div>
                            <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
                                <label className="checkbox-label es-base-toggle" style={{ width: '100%' }}>
                                    <input
                                        type="checkbox"
                                        checked={formData.es_base}
                                        onChange={(e) => handleInputChange('es_base', e.target.checked)}
                                    />
                                    <span className="checkbox-text">
                                        <strong>Producto Base</strong>
                                        <span className="checkbox-hint">Unidad base para equivalencias</span>
                                    </span>
                                </label>
                            </div>
                        </div>

                        {/* Sección de Facturación CFDI */}
                        <h4 className="section-title" style={{ marginTop: '1.5rem' }}>
                            <Receipt size={16} />
                            Datos para Facturación (CFDI)
                        </h4>

                        <div className="form-row-3">
                            <div className="form-group">
                                <Input
                                    label="Clave Producto/Servicio SAT"
                                    value={formData.cve_cfdi}
                                    onChange={(e) => handleInputChange('cve_cfdi', e.target.value)}
                                    placeholder="Ej: 01010101"
                                    maxLength={8}
                                    icon={<Receipt size={12} />}
                                    size="xs"
                                />
                                <span className="field-hint">Clave del catálogo c_ClaveProdServ del SAT</span>
                            </div>
                            <div className="form-group">
                                <label className="form-label">
                                    <FileText size={12} />
                                    Objeto de Impuesto
                                </label>
                                <Select
                                    value={objetosImpuesto.find(opt => opt.value === formData.objeto_imp) || null}
                                    onChange={(opt) => handleInputChange('objeto_imp', opt ? opt.value : '02')}
                                    options={objetosImpuesto}
                                    placeholder="Buscar..."
                                    styles={selectStyles}
                                    noOptionsMessage={() => 'Sin resultados'}
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">
                                    <Percent size={12} />
                                    Tasa de IVA
                                </label>
                                <Select
                                    value={tasasIVA.find(opt => opt.value === formData.tasa_iva) || null}
                                    onChange={(opt) => handleInputChange('tasa_iva', opt ? opt.value : '0.16')}
                                    options={tasasIVA}
                                    placeholder="Buscar..."
                                    styles={selectStyles}
                                    noOptionsMessage={() => 'Sin resultados'}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* Tab: Precios */}
                {activeTab === 'precios' && (
                    <div className="form-section">
                        <h4 className="section-title">
                            <DollarSign size={14} />
                            Precios de Venta
                        </h4>

                        {/* Fila 1: Precio Lista, Precio 1, Precio 2 */}
                        <div className="form-row-3">
                            <div className="form-group">
                                <Input
                                    ref={precioListaInputRef}
                                    type="number"
                                    label="Precio de Lista"
                                    value={formData.precio_lista}
                                    onChange={(e) => handleInputChange('precio_lista', e.target.value)}
                                    error={errors.precio_lista}
                                    placeholder="0.00"
                                    step="0.01"
                                    min="0"
                                    icon={<DollarSign size={12} />}
                                    size="xs"
                                />
                            </div>
                            <div className="form-group">
                                <Input
                                    type="number"
                                    label="Precio 1"
                                    value={formData.precio1}
                                    onChange={(e) => handleInputChange('precio1', e.target.value)}
                                    placeholder="0.00"
                                    step="0.01"
                                    min="0"
                                    icon={<DollarSign size={12} />}
                                    size="xs"
                                />
                            </div>
                            <div className="form-group">
                                <Input
                                    type="number"
                                    label="Precio 2"
                                    value={formData.precio2}
                                    onChange={(e) => handleInputChange('precio2', e.target.value)}
                                    placeholder="0.00"
                                    step="0.01"
                                    min="0"
                                    icon={<DollarSign size={12} />}
                                    size="xs"
                                />
                            </div>
                        </div>

                        {/* Fila 2: Precio 3, Precio 4, Precio 5 */}
                        <div className="form-row-3">
                            <div className="form-group">
                                <Input
                                    type="number"
                                    label="Precio 3"
                                    value={formData.precio3}
                                    onChange={(e) => handleInputChange('precio3', e.target.value)}
                                    placeholder="0.00"
                                    step="0.01"
                                    min="0"
                                    icon={<DollarSign size={12} />}
                                    size="xs"
                                />
                            </div>
                            <div className="form-group">
                                <Input
                                    type="number"
                                    label="Precio 4"
                                    value={formData.precio4}
                                    onChange={(e) => handleInputChange('precio4', e.target.value)}
                                    placeholder="0.00"
                                    step="0.01"
                                    min="0"
                                    icon={<DollarSign size={12} />}
                                    size="xs"
                                />
                            </div>
                            <div className="form-group">
                                <Input
                                    type="number"
                                    label="Precio 5"
                                    value={formData.precio5}
                                    onChange={(e) => handleInputChange('precio5', e.target.value)}
                                    placeholder="0.00"
                                    step="0.01"
                                    min="0"
                                    icon={<DollarSign size={12} />}
                                    size="xs"
                                />
                            </div>
                        </div>

                        {/* Fila 3: Precio 6 */}
                        <div className="form-row-3">
                            <div className="form-group">
                                <Input
                                    type="number"
                                    label="Precio 6"
                                    value={formData.precio6}
                                    onChange={(e) => handleInputChange('precio6', e.target.value)}
                                    placeholder="0.00"
                                    step="0.01"
                                    min="0"
                                    icon={<DollarSign size={12} />}
                                    size="xs"
                                />
                            </div>
                            <div className="form-group"></div>
                            <div className="form-group"></div>
                        </div>

                        {formData.precio_compra && formData.precio_lista && (
                            <div className="margin-info" style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', fontSize: '0.8rem', marginTop: '1rem' }}>
                                <span>
                                    <span className="margin-label">Lista:</span>
                                    <span className="margin-value" style={{ color: '#0284c7', fontWeight: '600' }}>
                                        {((parseFloat(formData.precio_lista) - parseFloat(formData.precio_compra)) / parseFloat(formData.precio_compra) * 100).toFixed(1)}%
                                    </span>
                                </span>
                                {formData.precio1 && parseFloat(formData.precio1) > 0 && (
                                    <span>
                                        <span className="margin-label">P1:</span>
                                        <span className="margin-value" style={{ color: '#16a34a', fontWeight: '600' }}>
                                            {((parseFloat(formData.precio1) - parseFloat(formData.precio_compra)) / parseFloat(formData.precio_compra) * 100).toFixed(1)}%
                                        </span>
                                    </span>
                                )}
                                {formData.precio2 && parseFloat(formData.precio2) > 0 && (
                                    <span>
                                        <span className="margin-label">P2:</span>
                                        <span className="margin-value" style={{ color: '#ca8a04', fontWeight: '600' }}>
                                            {((parseFloat(formData.precio2) - parseFloat(formData.precio_compra)) / parseFloat(formData.precio_compra) * 100).toFixed(1)}%
                                        </span>
                                    </span>
                                )}
                                {formData.precio3 && parseFloat(formData.precio3) > 0 && (
                                    <span>
                                        <span className="margin-label">P3:</span>
                                        <span className="margin-value" style={{ color: '#dc2626', fontWeight: '600' }}>
                                            {((parseFloat(formData.precio3) - parseFloat(formData.precio_compra)) / parseFloat(formData.precio_compra) * 100).toFixed(1)}%
                                        </span>
                                    </span>
                                )}
                                {formData.precio4 && parseFloat(formData.precio4) > 0 && (
                                    <span>
                                        <span className="margin-label">P4:</span>
                                        <span className="margin-value" style={{ color: '#7c3aed', fontWeight: '600' }}>
                                            {((parseFloat(formData.precio4) - parseFloat(formData.precio_compra)) / parseFloat(formData.precio_compra) * 100).toFixed(1)}%
                                        </span>
                                    </span>
                                )}
                                {formData.precio5 && parseFloat(formData.precio5) > 0 && (
                                    <span>
                                        <span className="margin-label">P5:</span>
                                        <span className="margin-value" style={{ color: '#db2777', fontWeight: '600' }}>
                                            {((parseFloat(formData.precio5) - parseFloat(formData.precio_compra)) / parseFloat(formData.precio_compra) * 100).toFixed(1)}%
                                        </span>
                                    </span>
                                )}
                                {formData.precio6 && parseFloat(formData.precio6) > 0 && (
                                    <span>
                                        <span className="margin-label">P6:</span>
                                        <span className="margin-value" style={{ color: '#0891b2', fontWeight: '600' }}>
                                            {((parseFloat(formData.precio6) - parseFloat(formData.precio_compra)) / parseFloat(formData.precio_compra) * 100).toFixed(1)}%
                                        </span>
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Tab: Descuentos */}
                {activeTab === 'descuentos' && (
                    <div className="form-section">
                        <div className="section-header-with-toggle">
                            <h4 className="section-title">
                                <Percent size={16} />
                                Descuentos por Volumen
                            </h4>
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={useDescuentosVolumen}
                                    onChange={(e) => setUseDescuentosVolumen(e.target.checked)}
                                />
                                <span className="checkbox-text">Activar descuentos</span>
                            </label>
                        </div>

                        {useDescuentosVolumen && (
                            <div className="descuentos-container">
                                <p className="descuentos-description">
                                    Configura descuentos sobre el <strong>Precio de Lista</strong> según la cantidad vendida.
                                </p>

                                {descuentosVolumen.length > 0 && (
                                    <div className="descuentos-list">
                                        <table className="descuentos-table">
                                            <thead>
                                                <tr>
                                                    <th style={{ width: '35%' }}>Cantidad Mínima</th>
                                                    <th style={{ width: '30%' }}>Descuento %</th>
                                                    <th style={{ width: '25%' }}>Precio Final</th>
                                                    <th style={{ width: '10%' }}>Acciones</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {descuentosVolumen.map((descuento, index) => (
                                                    <tr key={index}>
                                                        <td>
                                                            <Input
                                                                type="number"
                                                                value={descuento.cantidad_minima}
                                                                onChange={(e) => actualizarDescuento(index, 'cantidad_minima', e.target.value)}
                                                                placeholder="Ej: 10"
                                                                min="1"
                                                                step="1"
                                                                size="xs"
                                                                error={errors[`descuento_${index}_cantidad`]}
                                                            />
                                                        </td>
                                                        <td>
                                                            <Input
                                                                type="number"
                                                                value={descuento.descuento_porcentaje}
                                                                onChange={(e) => actualizarDescuento(index, 'descuento_porcentaje', e.target.value)}
                                                                placeholder="Ej: 5"
                                                                min="0.1"
                                                                max="100"
                                                                step="0.1"
                                                                size="xs"
                                                                error={errors[`descuento_${index}_porcentaje`]}
                                                            />
                                                        </td>
                                                        <td>
                                                            <span className="precio-preview">
                                                                ${calcularPrecioConDescuento(descuento.descuento_porcentaje).toFixed(2)}
                                                            </span>
                                                        </td>
                                                        <td style={{ textAlign: 'center' }}>
                                                            <button
                                                                type="button"
                                                                className="action-btn-compact danger"
                                                                onClick={() => eliminarDescuento(index)}
                                                                title="Eliminar"
                                                            >
                                                                <Trash2 size={14} />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                <Button
                                    type="button"
                                    variant="outline"
                                    size="xs"
                                    onClick={agregarDescuento}
                                    style={{ marginTop: '0.75rem' }}
                                >
                                    <Plus size={14} />
                                    Agregar Descuento
                                </Button>

                                {descuentosVolumen.length === 0 && (
                                    <div className="descuentos-empty">
                                        <Percent size={32} style={{ opacity: 0.3 }} />
                                        <p>No hay descuentos configurados</p>
                                        <p className="text-muted">Agrega descuentos para aplicar según cantidad</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Tab: Inventario */}
                {activeTab === 'inventario' && (
                    <div className="form-section">
                        <h4 className="section-title">
                            <Hash size={18} />
                            Inventario
                        </h4>

                        <div className="form-row">
                            <div className="form-group">
                                <Input
                                    ref={stockInputRef}
                                    type="number"
                                    label="Stock Actual"
                                    value={formData.stock}
                                    onChange={(e) => handleInputChange('stock', e.target.value)}
                                    error={errors.stock}
                                    placeholder="0"
                                    min="0"
                                    icon={<Package size={18} />}
                                />
                            </div>

                            <div className="form-group">
                                <Input
                                    type="number"
                                    label="Stock Mínimo"
                                    value={formData.stock_minimo}
                                    onChange={(e) => handleInputChange('stock_minimo', e.target.value)}
                                    error={errors.stock_minimo}
                                    placeholder="0"
                                    min="0"
                                    icon={<Package size={18} />}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* Tab: Equivalencias */}
                {activeTab === 'equivalencias' && (
                    <div className="form-section equivalencias-fullscreen">
                        <div className="section-header-with-toggle">
                            <h4 className="section-title">
                                <Link2 size={16} />
                                Equivalencias de Producto
                            </h4>
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={useEquivalencias}
                                    onChange={(e) => setUseEquivalencias(e.target.checked)}
                                />
                                <span className="checkbox-text">Activar equivalencias</span>
                            </label>
                        </div>

                        {useEquivalencias && (
                            <div className="equivalencias-container-fullscreen">
                                <p className="equiv-help-text">
                                    <strong>Equiv.</strong> = factor de conversión (ej: 12 pzas = 1 caja). <strong>Volumen</strong> = cantidad mínima para descuento. <strong>Desc. %</strong> = porcentaje de descuento a aplicar.
                                </p>

                                <div className="equiv-tabla-container">
                                    <table className="equiv-tabla">
                                        <thead>
                                            <tr>
                                                <th className="col-check"></th>
                                                <th className="col-unidad">Unidad</th>
                                                <th className="col-numero">Equiv.</th>
                                                <th className="col-numero">Volumen</th>
                                                <th className="col-numero">Desc. %</th>
                                                <th className="col-numero">Costo</th>
                                                <th className="col-numero">P. Lista</th>
                                                <th className="col-numero">Precio 1</th>
                                                <th className="col-numero">Precio 2</th>
                                                <th className="col-numero">Precio 3</th>
                                                <th className="col-numero">Precio 4</th>
                                                <th className="col-numero">Precio 5</th>
                                                <th className="col-numero">Precio 6</th>
                                                <th className="col-codigo">Código Corto</th>
                                                <th className="col-codigo">Código Barras</th>
                                                <th className="col-acciones"></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {/* Equivalencias existentes */}
                                            {equivalencias.map((equiv, index) => (
                                                <tr key={`exist-${index}`} className="row-existente">
                                                    <td className="col-check">
                                                        <span className="equiv-badge-creado">Creado</span>
                                                    </td>
                                                    <td className="col-unidad">
                                                        <div className="equiv-unidad-info">
                                                            <span className="unidad-activa">{equiv.producto_equivalente_nombre}</span>
                                                        </div>
                                                    </td>
                                                    <td className="col-numero">
                                                        <input
                                                            type="number"
                                                            value={equiv.cantidad || 1}
                                                            onChange={(e) => actualizarCantidadEquivalencia(index, e.target.value)}
                                                            min="0.001"
                                                            step="0.001"
                                                            className="equiv-input"
                                                            title="Factor de equivalencia"
                                                        />
                                                    </td>
                                                    <td className="col-numero">
                                                        <input
                                                            type="number"
                                                            value={equiv.volumen || ''}
                                                            onChange={(e) => actualizarPrecioEquivalencia(index, 'volumen', e.target.value)}
                                                            placeholder="0"
                                                            min="0"
                                                            step="1"
                                                            className="equiv-input"
                                                            title="Cantidad mínima para descuento"
                                                        />
                                                    </td>
                                                    <td className="col-numero">
                                                        <input
                                                            type="number"
                                                            value={equiv.descuento || ''}
                                                            onChange={(e) => actualizarPrecioEquivalencia(index, 'descuento', e.target.value)}
                                                            placeholder="0"
                                                            min="0"
                                                            max="100"
                                                            step="0.1"
                                                            className="equiv-input"
                                                            title="Porcentaje de descuento"
                                                        />
                                                    </td>
                                                    <td className="col-numero">
                                                        <input
                                                            type="number"
                                                            value={equiv.costo || ''}
                                                            onChange={(e) => actualizarPrecioEquivalencia(index, 'costo', e.target.value)}
                                                            placeholder="0.00"
                                                            step="0.01"
                                                            min="0"
                                                            className="equiv-input"
                                                        />
                                                    </td>
                                                    <td className="col-numero">
                                                        <input
                                                            type="number"
                                                            value={equiv.precio_lista || ''}
                                                            onChange={(e) => actualizarPrecioEquivalencia(index, 'precio_lista', e.target.value)}
                                                            placeholder="0.00"
                                                            step="0.01"
                                                            min="0"
                                                            className="equiv-input"
                                                        />
                                                    </td>
                                                    <td className="col-numero">
                                                        <input
                                                            type="number"
                                                            value={equiv.precio1 || ''}
                                                            onChange={(e) => actualizarPrecioEquivalencia(index, 'precio1', e.target.value)}
                                                            placeholder="0.00"
                                                            step="0.01"
                                                            min="0"
                                                            className="equiv-input"
                                                        />
                                                    </td>
                                                    <td className="col-numero">
                                                        <input
                                                            type="number"
                                                            value={equiv.precio2 || ''}
                                                            onChange={(e) => actualizarPrecioEquivalencia(index, 'precio2', e.target.value)}
                                                            placeholder="0.00"
                                                            step="0.01"
                                                            min="0"
                                                            className="equiv-input"
                                                        />
                                                    </td>
                                                    <td className="col-numero">
                                                        <input
                                                            type="number"
                                                            value={equiv.precio3 || ''}
                                                            onChange={(e) => actualizarPrecioEquivalencia(index, 'precio3', e.target.value)}
                                                            placeholder="0.00"
                                                            step="0.01"
                                                            min="0"
                                                            className="equiv-input"
                                                        />
                                                    </td>
                                                    <td className="col-numero">
                                                        <input
                                                            type="number"
                                                            value={equiv.precio4 || ''}
                                                            onChange={(e) => actualizarPrecioEquivalencia(index, 'precio4', e.target.value)}
                                                            placeholder="0.00"
                                                            step="0.01"
                                                            min="0"
                                                            className="equiv-input"
                                                        />
                                                    </td>
                                                    <td className="col-numero">
                                                        <input
                                                            type="number"
                                                            value={equiv.precio5 || ''}
                                                            onChange={(e) => actualizarPrecioEquivalencia(index, 'precio5', e.target.value)}
                                                            placeholder="0.00"
                                                            step="0.01"
                                                            min="0"
                                                            className="equiv-input"
                                                        />
                                                    </td>
                                                    <td className="col-numero">
                                                        <input
                                                            type="number"
                                                            value={equiv.precio6 || ''}
                                                            onChange={(e) => actualizarPrecioEquivalencia(index, 'precio6', e.target.value)}
                                                            placeholder="0.00"
                                                            step="0.01"
                                                            min="0"
                                                            className="equiv-input"
                                                        />
                                                    </td>
                                                    <td className="col-codigo">
                                                        <input
                                                            type="text"
                                                            value={equiv.codigo_corto || ''}
                                                            onChange={(e) => actualizarPrecioEquivalencia(index, 'codigo_corto', e.target.value)}
                                                            placeholder="Código corto"
                                                            className="equiv-input codigo"
                                                        />
                                                    </td>
                                                    <td className="col-codigo">
                                                        <input
                                                            type="text"
                                                            value={equiv.codigo_barras || ''}
                                                            onChange={(e) => actualizarPrecioEquivalencia(index, 'codigo_barras', e.target.value)}
                                                            placeholder="Código de barras"
                                                            className="equiv-input codigo"
                                                        />
                                                    </td>
                                                    <td className="col-acciones">
                                                        <button
                                                            type="button"
                                                            className="action-btn-compact danger"
                                                            onClick={() => eliminarEquivalencia(index)}
                                                            title="Eliminar"
                                                        >
                                                            <Trash2 size={14} />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}

                                            {/* Nuevas unidades para crear */}
                                            {tablaEquivalentes.map((eq) => (
                                                <tr key={eq.key} className={eq.activo ? 'row-activo' : 'row-inactivo'}>
                                                    <td className="col-check">
                                                        <input
                                                            type="checkbox"
                                                            checked={eq.activo}
                                                            onChange={() => toggleEquivalenteActivo(eq.key)}
                                                            className="equiv-checkbox"
                                                        />
                                                    </td>
                                                    <td className="col-unidad">
                                                        <span className={eq.activo ? 'unidad-activa' : ''}>{eq.label}</span>
                                                    </td>
                                                    <td className="col-numero">
                                                        <input
                                                            type="number"
                                                            value={eq.equivalencia}
                                                            onChange={(e) => actualizarCampoEquivalente(eq.key, 'equivalencia', e.target.value)}
                                                            disabled={!eq.activo}
                                                            placeholder="1"
                                                            step="0.001"
                                                            min="0.001"
                                                            className="equiv-input"
                                                            title="Factor de equivalencia"
                                                        />
                                                    </td>
                                                    <td className="col-numero">
                                                        <input
                                                            type="number"
                                                            value={eq.volumen}
                                                            onChange={(e) => actualizarCampoEquivalente(eq.key, 'volumen', e.target.value)}
                                                            disabled={!eq.activo}
                                                            placeholder="0"
                                                            step="1"
                                                            min="0"
                                                            className="equiv-input"
                                                            title="Cantidad mínima para descuento"
                                                        />
                                                    </td>
                                                    <td className="col-numero">
                                                        <input
                                                            type="number"
                                                            value={eq.descuento}
                                                            onChange={(e) => actualizarCampoEquivalente(eq.key, 'descuento', e.target.value)}
                                                            disabled={!eq.activo}
                                                            placeholder="0"
                                                            step="0.1"
                                                            min="0"
                                                            max="100"
                                                            className="equiv-input"
                                                            title="Porcentaje de descuento"
                                                        />
                                                    </td>
                                                    <td className="col-numero">
                                                        <input
                                                            type="number"
                                                            value={eq.costo}
                                                            onChange={(e) => actualizarCampoEquivalente(eq.key, 'costo', e.target.value)}
                                                            disabled={!eq.activo}
                                                            placeholder="0.00"
                                                            step="0.01"
                                                            min="0"
                                                            className="equiv-input"
                                                        />
                                                    </td>
                                                    <td className="col-numero">
                                                        <input
                                                            type="number"
                                                            value={eq.precio_lista}
                                                            onChange={(e) => actualizarCampoEquivalente(eq.key, 'precio_lista', e.target.value)}
                                                            disabled={!eq.activo}
                                                            placeholder="0.00"
                                                            step="0.01"
                                                            min="0"
                                                            className="equiv-input"
                                                        />
                                                    </td>
                                                    <td className="col-numero">
                                                        <input
                                                            type="number"
                                                            value={eq.precio1}
                                                            onChange={(e) => actualizarCampoEquivalente(eq.key, 'precio1', e.target.value)}
                                                            disabled={!eq.activo}
                                                            placeholder="0.00"
                                                            step="0.01"
                                                            min="0"
                                                            className="equiv-input"
                                                        />
                                                    </td>
                                                    <td className="col-numero">
                                                        <input
                                                            type="number"
                                                            value={eq.precio2}
                                                            onChange={(e) => actualizarCampoEquivalente(eq.key, 'precio2', e.target.value)}
                                                            disabled={!eq.activo}
                                                            placeholder="0.00"
                                                            step="0.01"
                                                            min="0"
                                                            className="equiv-input"
                                                        />
                                                    </td>
                                                    <td className="col-numero">
                                                        <input
                                                            type="number"
                                                            value={eq.precio3}
                                                            onChange={(e) => actualizarCampoEquivalente(eq.key, 'precio3', e.target.value)}
                                                            disabled={!eq.activo}
                                                            placeholder="0.00"
                                                            step="0.01"
                                                            min="0"
                                                            className="equiv-input"
                                                        />
                                                    </td>
                                                    <td className="col-numero">
                                                        <input
                                                            type="number"
                                                            value={eq.precio4}
                                                            onChange={(e) => actualizarCampoEquivalente(eq.key, 'precio4', e.target.value)}
                                                            disabled={!eq.activo}
                                                            placeholder="0.00"
                                                            step="0.01"
                                                            min="0"
                                                            className="equiv-input"
                                                        />
                                                    </td>
                                                    <td className="col-numero">
                                                        <input
                                                            type="number"
                                                            value={eq.precio5}
                                                            onChange={(e) => actualizarCampoEquivalente(eq.key, 'precio5', e.target.value)}
                                                            disabled={!eq.activo}
                                                            placeholder="0.00"
                                                            step="0.01"
                                                            min="0"
                                                            className="equiv-input"
                                                        />
                                                    </td>
                                                    <td className="col-numero">
                                                        <input
                                                            type="number"
                                                            value={eq.precio6}
                                                            onChange={(e) => actualizarCampoEquivalente(eq.key, 'precio6', e.target.value)}
                                                            disabled={!eq.activo}
                                                            placeholder="0.00"
                                                            step="0.01"
                                                            min="0"
                                                            className="equiv-input"
                                                        />
                                                    </td>
                                                    <td className="col-codigo">
                                                        <input
                                                            type="text"
                                                            value={eq.codigo_corto || ''}
                                                            onChange={(e) => actualizarCampoEquivalente(eq.key, 'codigo_corto', e.target.value)}
                                                            disabled={!eq.activo}
                                                            placeholder="Código corto"
                                                            className="equiv-input codigo"
                                                        />
                                                    </td>
                                                    <td className="col-codigo">
                                                        <input
                                                            type="text"
                                                            value={eq.codigo_asociado}
                                                            onChange={(e) => actualizarCampoEquivalente(eq.key, 'codigo_asociado', e.target.value)}
                                                            disabled={!eq.activo}
                                                            placeholder="Código de barras"
                                                            className="equiv-input codigo"
                                                        />
                                                    </td>
                                                    <td className="col-acciones"></td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Resumen */}
                                <div className="equiv-footer-actions">
                                    {equivalencias.length > 0 && (
                                        <span className="equiv-resumen-texto">
                                            Equivalencias existentes: <strong>{equivalencias.length}</strong>
                                        </span>
                                    )}
                                    {tablaEquivalentes.filter(eq => eq.activo).length > 0 && (
                                        <span className="equiv-resumen-texto equiv-por-crear">
                                            Se crearán <strong>{tablaEquivalentes.filter(eq => eq.activo).length}</strong> producto(s) equivalente(s) al guardar
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Acciones */}
            <div className="form-actions">
                {!renderHeaderToggle && (
                    <div className="form-actions-left">
                        <label className="toggle-switch-form">
                            <input
                                type="checkbox"
                                checked={formData.activo}
                                onChange={(e) => handleInputChange('activo', e.target.checked)}
                            />
                            <span className="toggle-slider-form"></span>
                            <span className="toggle-label">{formData.activo ? 'Activo' : 'Inactivo'}</span>
                        </label>
                    </div>
                )}
                <div className="form-actions-right">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={onCancel}
                        disabled={isLoading}
                        size="xs"
                    >
                        Cancelar
                    </Button>

                    <Button
                        type="submit"
                        disabled={isLoading}
                        size="xs"
                    >
                        {isEdit ? 'Actualizar' : 'Crear'} Producto
                    </Button>
                </div>
            </div>
        </form>
    );
};

export default ProductForm;
